/**
 * Computes the breakdown / column-key parts of a dataset instance's config
 * from the deliverable's API definition.
 *
 * Called from the toggle/add paths only:
 *   - Step 1's `toggleDatasetType` (new selection)
 *   - Step 2's dataset switcher (`addDatasetInstance`, e.g. duplicate)
 *
 * Hydration (`hydrateStoreFromPayload`) does NOT call this — the saved payload
 * already lists the included files explicitly, so hydration just copies that
 * list into `includedBreakdownIds` directly.
 */
import type { DatasetInstanceConfig } from '../data-builder.store';
import type { DeliverableColumnConfig } from './deliverable-column-config';

const AGGREGATION_BREAKDOWN_ID = 'aggregation';

type ColumnOptionLike = {
  key: string;
  isDefault: boolean;
  isRequired: boolean;
};

export const collectDefaultColumnKeys = (
  options: ColumnOptionLike[]
): string[] => {
  const seen = new Set<string>();
  const keys: string[] = [];
  for (const o of options) {
    if ((o.isDefault || o.isRequired) && !seen.has(o.key)) {
      seen.add(o.key);
      keys.push(o.key);
    }
  }
  return keys;
};

export type SeededInstanceConfig = Pick<
  DatasetInstanceConfig,
  'columnKeysByBreakdown' | 'includedBreakdownIds'
>;

/**
 * Derive the parts of a fresh instance config that depend on the
 * deliverable's schema:
 *   - `includedBreakdownIds` — the schema's default-flagged breakdowns
 *   - `columnKeysByBreakdown` — default + required keys for the aggregation
 *     and each included breakdown
 */
export const seedInstanceDefaults = (
  deliverableConfig: DeliverableColumnConfig
): SeededInstanceConfig => {
  const includedBreakdownIds = deliverableConfig.breakdowns
    .filter((b) => b.isDefault)
    .map((b) => b.id);

  const columnKeysByBreakdown: Record<string, string[]> = {};

  const aggregationKeys = collectDefaultColumnKeys(
    deliverableConfig.aggregationColumns.options
  );
  if (aggregationKeys.length > 0) {
    columnKeysByBreakdown[AGGREGATION_BREAKDOWN_ID] = aggregationKeys;
  }

  const includedSet = new Set(includedBreakdownIds);
  for (const breakdown of deliverableConfig.breakdowns) {
    if (!includedSet.has(breakdown.id)) continue;
    const keys = collectDefaultColumnKeys(breakdown.columnOptions);
    if (keys.length > 0) {
      columnKeysByBreakdown[breakdown.id] = keys;
    }
  }

  return {
    includedBreakdownIds,
    columnKeysByBreakdown,
  };
};
